import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * DropdownMenu - A reusable dropdown menu component
 *
 * Touch-optimized (SAT-91):
 * - 44px minimum touch targets
 * - Long press support for context menus
 * - No hover-dependent features
 *
 * @param {React.ReactNode} trigger - The element that triggers the dropdown
 * @param {Array} items - Menu items [{label, icon?, onClick, divider?, disabled?, submenu?}]
 * @param {string} align - Alignment ('left' | 'right'), defaults to 'right'
 * @param {boolean} longPressEnabled - Enable long press to open (default: true)
 * @param {number} longPressDelay - Long press delay in ms (default: 500)
 */
function DropdownMenu({ trigger, items, align = 'right', longPressEnabled = true, longPressDelay = 500 }) {
  const [isOpen, setIsOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const longPressTimer = useRef(null);
  const isLongPressRef = useRef(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSubmenuOpen(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSubmenuOpen(null);
        setMenuPosition(null);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Clean up long press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  // Long press handlers for touch devices (SAT-91)
  const handleTouchStart = useCallback((e) => {
    if (!longPressEnabled) return;

    isLongPressRef.current = false;
    const touch = e.touches[0];

    longPressTimer.current = setTimeout(() => {
      isLongPressRef.current = true;

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      // Position menu near touch point
      setMenuPosition({ x: touch.clientX, y: touch.clientY });
      setIsOpen(true);
    }, longPressDelay);
  }, [longPressEnabled, longPressDelay]);

  const handleTouchMove = useCallback(() => {
    // Cancel long press if user moves finger
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // Prevent click if it was a long press
    if (isLongPressRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleItemClick = (item) => {
    if (item.disabled) return;
    if (item.submenu) {
      setSubmenuOpen(submenuOpen === item.label ? null : item.label);
      return;
    }
    item.onClick?.();
    setIsOpen(false);
    setSubmenuOpen(null);
  };

  return (
    <div className="relative inline-block">
      {/* Trigger - with long press support for touch (SAT-91) */}
      <div
        ref={triggerRef}
        onClick={() => {
          if (!isLongPressRef.current) {
            setIsOpen(!isOpen);
            setMenuPosition(null);
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        className="cursor-pointer touch-manipulation"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {trigger}
      </div>

      {/* Menu - with touch-optimized sizing (SAT-91) */}
      {isOpen && (
        <div
          ref={menuRef}
          style={menuPosition ? {
            position: 'fixed',
            left: Math.min(menuPosition.x, window.innerWidth - 200),
            top: Math.min(menuPosition.y, window.innerHeight - 300),
          } : undefined}
          className={`
            ${menuPosition ? 'fixed' : 'absolute mt-2'}
            z-50 min-w-[200px] py-2
            bg-white dark:bg-gray-800
            border border-gray-200 dark:border-gray-700
            rounded-xl shadow-xl
            animate-fadeInScale gpu-accelerated
            ${!menuPosition && (align === 'right' ? 'right-0' : 'left-0')}
          `}
          role="menu"
        >
          {items.map((item, index) => {
            if (item.divider) {
              return (
                <div
                  key={`divider-${index}`}
                  className="my-1 border-t border-gray-200 dark:border-gray-700"
                />
              );
            }

            const hasSubmenu = item.submenu && item.submenu.length > 0;

            return (
              <div key={item.label} className="relative">
                <button
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  className={`
                    w-full flex items-center justify-between gap-3 px-4 py-3
                    min-h-[44px] text-left text-sm touch-manipulation
                    ${item.disabled
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600'
                    }
                    transition-colors
                  `}
                  role="menuitem"
                >
                  <span className="flex items-center gap-3">
                    {item.icon && (
                      <span className="w-5 h-5 shrink-0">
                        {item.icon}
                      </span>
                    )}
                    <span>{item.label}</span>
                  </span>
                  {hasSubmenu && (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>

                {/* Submenu - with touch-optimized sizing (SAT-91) */}
                {hasSubmenu && submenuOpen === item.label && (
                  <div
                    className={`
                      absolute top-0 min-w-[180px] py-2
                      bg-white dark:bg-gray-800
                      border border-gray-200 dark:border-gray-700
                      rounded-xl shadow-xl
                      ${align === 'right' ? 'right-full mr-1' : 'left-full ml-1'}
                    `}
                    role="menu"
                  >
                    {item.submenu.map((subitem) => (
                      <button
                        key={subitem.label}
                        onClick={() => {
                          if (!subitem.disabled) {
                            subitem.onClick?.();
                            setIsOpen(false);
                            setSubmenuOpen(null);
                            setMenuPosition(null);
                          }
                        }}
                        disabled={subitem.disabled}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3
                          min-h-[44px] text-left text-sm touch-manipulation
                          ${subitem.disabled
                            ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600'
                          }
                          transition-colors
                        `}
                        role="menuitem"
                      >
                        {subitem.icon && (
                          <span className="w-5 h-5 shrink-0">
                            {subitem.icon}
                          </span>
                        )}
                        <span>{subitem.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DropdownMenu;
